import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'

import ApiClient from '@src/lib/api_client'
import Member, { MemberState } from '@src/data/member'
import { API } from '@src/lib/api_path'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Input } from '@src/elements/input/input'
import { Button } from '@src/elements/button/button'
import { Checkbox } from '@src/elements/input/checkbox'
import { DatePicker } from '@src/elements/input/date-picker'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { NotificationTypes } from '@src/util/constants'
import EventBus from '@src/lib/event_bus'
import { ContentBox } from '@src/elements/content-box/content-box'
import { Modal } from '@src/elements/modal/modal'
import { InputFootnote } from '@src/elements/input/input-footnote'
import { noop } from '@src/util/noop'
import SessionManager from '@src/lib/session_manager'
import { TrainingPlanService } from '@src/service/training-plan'
import { WorkoutService } from '@src/service/workout'
import { convertToLocalDate, convertToYYYYMMDD } from '@src/util/date-format'
import { getTomorrowDate } from '@src/util/date-utilities'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    }
  }
})
export class MemberActions extends Vue {

  protected member: Member

  protected apiClient: ApiClient = new ApiClient()

  protected confirmPopup: any = {
    isVisible: false,
    message: '',
    callback: noop
  }
  private isQrFormVisible: boolean = false
  private qrCode: string = ''
  private codeError: boolean = false
  private scheduledDeactivationEnabled: boolean = false
  private scheduledDeactivationDate: string = ''
  private lastSavedScheduledDeactivationDate: string = ''
  private isSavingScheduledDeactivation: boolean = false

  constructor () {
    super()
  }

  mounted () {
    this.scheduledDeactivationEnabled = !!this.member.scheduledDeactivationDate
    this.scheduledDeactivationDate = this.member.scheduledDeactivationDate ? convertToLocalDate(this.member.scheduledDeactivationDate) : ''
    this.lastSavedScheduledDeactivationDate = this.member.scheduledDeactivationDate || ''
  }

  @Watch('member.id')
  onSelectedMemberChanged () {
    this.scheduledDeactivationEnabled = !!this.member.scheduledDeactivationDate
    this.scheduledDeactivationDate = this.member.scheduledDeactivationDate ? convertToLocalDate(this.member.scheduledDeactivationDate) : ''
    this.lastSavedScheduledDeactivationDate = this.member.scheduledDeactivationDate || ''
  }

  @Watch('member.scheduledDeactivationDate')
  onScheduledDeactivationDateChanged () {
    this.scheduledDeactivationEnabled = !!this.member.scheduledDeactivationDate
    this.scheduledDeactivationDate = this.member.scheduledDeactivationDate ? convertToLocalDate(this.member.scheduledDeactivationDate) : ''
    if (this.member.scheduledDeactivationDate) {
      this.lastSavedScheduledDeactivationDate = this.member.scheduledDeactivationDate
    }
  }

  showTrashConfirm () {
    this.confirmPopup = {
      isVisible: true,
      message: SessionManager.Instance.isManager() ? this.$t('PAGE.MEMBERS.STATUS.CONFIRM_REMOVE') : this.$t('PAGE.MEMBERS.STATUS.CONFIRM_DELETE'),
      callback: this.trash
    }
  }

  trash (confirm) {
    if (confirm === true) {
      return this.apiClient.post(API.TRAINERS_API.MEMBER_TRASH.replace('{memberId}', this.member.id.toString()), {})
        .then(() => {
          this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.STATUS.REMOVE_SUCCESSFULLY'), fadeOut: true })
          this.$router.push('/trainers/platform/members')
        })
        .catch((error) => console.log('cannot trash member', error))
    }
    this.hidePopup()
  }

  showDeactivateConfirm () {
    this.confirmPopup = {
      isVisible: true,
      message: this.$t('PAGE.MEMBERS.STATUS.CONFIRM_DEACTIVATE'),
      callback: this.deactivate
    }
  }

  deactivate (confirm) {
    if (confirm === true) {
      return this.clearScheduledDeactivationDate()
        .then(() => this.apiClient.post(API.TRAINERS_API.MEMBER_DEACTIVATE.replace('{memberId}', this.member.id.toString()), {}))
        .then(() => {
          this.member.scheduledDeactivationDate = null
          this.scheduledDeactivationEnabled = false
          this.scheduledDeactivationDate = ''
          this.lastSavedScheduledDeactivationDate = ''
          this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.STATUS.DEACTIVATE_SUCCESSFULLY'), fadeOut: true })
          EventBus.trigger('reloadSelectedMember')
          this.hidePopup()
          return this.deleteCurrentTrainingPlan().then(() => {
            return this.setDefaultWorkout()
          })
        })
        .catch((error) => console.log('cannot deactivate member', error))
    }
    this.hidePopup()
  }

  clearScheduledDeactivationDate () {
    if (!this.member.scheduledDeactivationDate) {
      return Promise.resolve()
    }

    return this.apiClient.put(`${API.TRAINERS_API.MEMBERS}/${this.member.id}`, { member: { scheduled_deactivation_date: null } })
      .then(() => {
        this.member.scheduledDeactivationDate = null
        this.scheduledDeactivationEnabled = false
        this.scheduledDeactivationDate = ''
        this.lastSavedScheduledDeactivationDate = ''
      })
  }

  async deleteCurrentTrainingPlan () {
    if (this.member.currentTrainingPlan) {
      let tpService = new TrainingPlanService()
      await tpService.delete(this.member.currentTrainingPlan.id)
    }
  }

  async setDefaultWorkout () {
    // Activate the default station workout
    let woService = new WorkoutService()
    let defaultWorkout = await woService.getDefaultWorkout()
    if (defaultWorkout && defaultWorkout.id) {
      return woService.activateWorkout(defaultWorkout.id, this.member.id)
    }
  }

  hidePopup () {
    this.confirmPopup = {
      isVisible: false
    }
  }

  showQRForm () {
    this.isQrFormVisible = true
  }

  hideQrForm () {
    this.isQrFormVisible = false
  }

  setScheduledDeactivationEnabled (value: boolean) {
    this.scheduledDeactivationEnabled = value
    if (!value) {
      this.scheduledDeactivationDate = ''
    }
  }

  onScheduledDeactivationDateSelected (value: string) {
    this.scheduledDeactivationDate = value || ''
  }

  saveScheduledDeactivation () {
    this.isSavingScheduledDeactivation = true
    const scheduledDeactivationDate = this.scheduledDeactivationEnabled && this.scheduledDeactivationDate
      ? convertToYYYYMMDD(this.scheduledDeactivationDate)
      : null

    return this.apiClient.put(`${API.TRAINERS_API.MEMBERS}/${this.member.id}`, { member: { scheduled_deactivation_date: scheduledDeactivationDate } })
      .then(() => {
        this.member.scheduledDeactivationDate = scheduledDeactivationDate
        this.scheduledDeactivationDate = scheduledDeactivationDate ? convertToLocalDate(scheduledDeactivationDate) : ''
        this.lastSavedScheduledDeactivationDate = scheduledDeactivationDate || ''
        this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.ACCOUNT.SCHEDULED-DEACTIVATION-UPDATED-SUCCESSFULLY'), fadeOut: true })
        EventBus.trigger('reloadSelectedMember')
        this.isSavingScheduledDeactivation = false
      })
      .catch((error) => {
        console.log('cannot update scheduled deactivation', error)
        this.isSavingScheduledDeactivation = false
      })
  }

  get scheduledDeactivationDisplayDate () {
    if (!this.lastSavedScheduledDeactivationDate) {
      return ''
    }
    return convertToLocalDate(this.lastSavedScheduledDeactivationDate)
  }

  get isMemberAccountRoute () {
    if (!this.$route) return false
    return this.$route.path.startsWith('/members/') && this.$route.path.endsWith('/account')
  }
  get isMemberDeactivated () {
    return this.member.state === MemberState.DEACTIVATED
  }

  reactivate () {
    return this.apiClient.post(API.TRAINERS_API.MEMBER_ACTIVATE.replace('{memberId}', this.member.id.toString()).replace('{codeSerial}', this.qrCode), {})
      .then(() => {
        this.codeError = false
        this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.STATUS.ACTIVATE_SUCCESSFULLY'), fadeOut: true })
        EventBus.trigger('reloadSelectedMember')
        this.hideQrForm()
      })
      .catch((error) => {
        this.codeError = true
        console.log('cannot activate member', error)
      })
  }

  render (h: CreateElement) {
    return <div class='member-actions'>
      <ContentBox>
        <div slot='content'>
          <Subtitle1>{this.$t('PAGE.MEMBERS.STATUS.ACTIONS')}</Subtitle1>
        </div>
        <div class='member-actions-bottom' slot='bottom'>
          <div class='member-actions-left'>
            <div class={{ 'scheduled-deactivation-controls': true, 'disabled': this.isMemberAccountRoute || this.isMemberDeactivated }}>
              <div class='scheduled-deactivation-title'>{this.$t('PAGE.MEMBERS.SCHEDULED-DEACTIVATION')}</div>
              <div class='scheduled-deactivation-row'>
                <div class='scheduled-deactivation-checkbox'>
                  <Checkbox value={this.scheduledDeactivationEnabled}
                            change={this.setScheduledDeactivationEnabled}
                            disabled={this.isMemberAccountRoute || this.isMemberDeactivated}
                            text={this.$t('PAGE.MEMBERS.ENABLE-SCHEDULED-DEACTIVATION')} />
                </div>

                <div class='scheduled-deactivation-calendar'>
                  <DatePicker ref='scheduledDeactivationDate'
                              type='date'
                              value={this.scheduledDeactivationDate}
                              change={this.onScheduledDeactivationDateSelected}
                              allowPastDate={false}
                              firstAvailableDate={getTomorrowDate()}
                              disabled={this.isMemberAccountRoute || !this.scheduledDeactivationEnabled || this.isMemberDeactivated}
                              popper-class={'over-modal'}
                              label={this.$t('DATE')} />
                </div>
              </div>

              <Button class='scheduled-deactivation-save-btn trash-btn'
                      label={this.$t('SAVE')}
                      type='elevated'
                      disabled={ this.isMemberAccountRoute || this.isMemberDeactivated ||
                        (!this.scheduledDeactivationEnabled && !this.lastSavedScheduledDeactivationDate) ||
                        (this.scheduledDeactivationEnabled && !this.scheduledDeactivationDate) ||
                        (this.scheduledDeactivationEnabled && !!this.scheduledDeactivationDate &&
                          convertToYYYYMMDD(this.scheduledDeactivationDate) === this.lastSavedScheduledDeactivationDate) }
                      loading={this.isSavingScheduledDeactivation}
                      click={this.saveScheduledDeactivation} />
            </div>

            {(this.lastSavedScheduledDeactivationDate && !this.isMemberDeactivated) &&
              <div class='scheduled-deactivation-message'>
                {`${this.$t('PAGE.MEMBERS.SCHEDULED-DEACTIVATION-DATE')} ${this.scheduledDeactivationDisplayDate}`}
              </div>
            }
          </div>

          <div id='deactivation' class='member-actions-right'>
            <div class='immediate-action-title'>{this.$t('PAGE.MEMBERS.IMMEDIATE-ACTION')}</div>
            <div class='immediate-action-controls'>
              {this.renderBlockActions(h)}
              {this.renderDeleteAction(h)}
            </div>
          </div>
        </div>
      </ContentBox>

      <ConfirmModal show={this.confirmPopup.isVisible}
                    closeCallback={this.confirmPopup.callback || this.hidePopup}
                    message={this.confirmPopup.message}
                    type={NotificationTypes.CONFIRM} />

      <Modal show={this.isQrFormVisible}
             title={this.$t('PAGE.MEMBERS.QR_CODE.ASSIGN')}
             closeCallback={this.hideQrForm}
             cssClass='reactivation-qr-code'>
        <div>
          <Input ref='qrInput' class={this.codeError ? 'error' : ''} value={this.qrCode} valueChanged={(value) => this.qrCode = value} label={this.$t('PAGE.MEMBERS.QR_CODE.NEW')} />
          {this.codeError ? <InputFootnote class='error'>{this.$t('PAGE.MEMBERS.CREATE.ERROR.INVALID_QR_CODE.TITLE')}</InputFootnote> : null}
        </div>

        <div class='btn-row' slot='footer'>
          <Button class='cancel-btn' type='outline' label={this.$t('CANCEL')} click={this.hideQrForm} ref='cancelBtn' />
          <Button class='save-btn' type='elevated' label={this.$t('SAVE')} click={this.reactivate} ref='saveBtn' />
        </div>
      </Modal>
    </div>
  }

  renderBlockActions (h: CreateElement) {
    return this.isMemberDeactivated ?
      <Button class='unblock-btn' ref='unblock-btn' label={this.$t('PAGE.MEMBERS.STATUS.ACTIVATE')} click={this.showQRForm} type='outline' />
      : <Button class='block-btn' ref='block-btn' label={this.$t('PAGE.MEMBERS.STATUS.DEACTIVATE')} click={this.showDeactivateConfirm} type='outline' />
  }

  renderDeleteAction (h: CreateElement) {
    return <Button class='trash-btn' ref='trash-btn' label={this.$t('PAGE.MEMBERS.STATUS.REMOVE_FROM_CLUB')} click={this.showTrashConfirm} type='elevated' />
  }
}
